import { property, query, state } from 'lit/decorators.js';
import { BaseComponent } from '../base-component';

import { booleanConverter } from '../../../common/converters';
import { waitForElementsToLoad } from '../../../common/utils/delay-util';
import { wait } from '../../../common/utils/util';
import throttle from 'lodash/throttle';

interface ValidationMessageType {
  [key: string]: string | object | Function;
}

export enum FORM_CONST {
  hideErrorMessage = "HIDE_ERROR_MESSAGE"
}

export class FormBaseComponent extends BaseComponent {
  @query('form')
  protected formElement: HTMLFormElement | undefined;

  /*
   * form is modified/touched
   */
  @property({ converter: booleanConverter })
  isDirty = false;

  @property()
  errorMessage = '';

  @property({ converter: booleanConverter })
  focusOnFirstField = false;

  @state()
  protected isSubmitted = false;

  protected validationEventList = ['blur', 'change'];

  protected readonly FORM_SUBMIT_EVENT = 'form-submit';
  /*
   * validityState to message key mapping
   */
  private errorMessageKeyMapping = {
    valueMissing: 'required',
    badInput: 'required',
    patternMismatch: 'pattern',
    rangeOverflow: 'max',
    rangeUnderflow: 'min',
    stepMismatch: 'step',
    tooLong: 'maxlength',
    typeMismatch: 'minlength',
    customError: 'customError',
  };

  protected async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    await wait(0); // waiting for first paint to complete
    await waitForElementsToLoad(this.shadowRoot!, ['form']); // wait for form to render
    this.bindListnersForInputChangeEvent();
  }

  protected updated(_changedProperties: Map<string | number | symbol, unknown>): void {
    super.updated(_changedProperties);
    const oldIsDirty = _changedProperties.get('isDirty');
    if (typeof oldIsDirty != 'undefined' && oldIsDirty != this.isDirty) this.validateFields();
  }

  private inputElementList() {
    return this.formElement?.querySelectorAll('input:valid, input:invalid, textarea:valid, textarea:invalid, select:valid, select:invalid');
  }

  protected async bindListnersForInputChangeEvent() {
    await wait(0);
    this.inputElementList()?.forEach((el) => {
      this.validationEventList.forEach((eventName) => {
        el.removeEventListener(eventName, this.handleInputEvent);
        el.addEventListener(eventName, this.handleInputEvent);
      });
    });

    // listen to form-submit event and update validation when nested form(inside custom element) are submitted
    this.shadowRoot?.addEventListener(this.FORM_SUBMIT_EVENT, () => this.validateFields());
  }

  private handleInputEvent = (event: Event) => {
    const el = event.target as HTMLInputElement;
    this.checkValidation(el);
  };

  async validateFields() {
    const activeElement = this.shadowRoot?.activeElement;
    const lastInteractionEvent = activeElement?.getAttribute('data-last-interaction');
    const isUnitButton = activeElement?.id === 'addUnitOptionLink';

    // EUA-1729: skip validation if it's a click/keyup event on unit button
    if (isUnitButton && (lastInteractionEvent === 'click' || lastInteractionEvent === 'keyup')) {
      return;
    }

    await wait(0); // wait for values to be updated in input element and then check for validation
    this.isDirty = true;
    this.inputElementList()?.forEach((_el) => this.checkValidation(_el as HTMLInputElement));
  }

  protected submit(e) {
    // to disable form submit default behaviour
    e.preventDefault();
    e.stopPropagation();

    if (this.formElement?.checkValidity() === false) this.validateFields();
  }

  private checkValidation = throttle(async (inputElement: HTMLInputElement) => {

    const errorValidator = this.customValidator()?.[inputElement.id];
    let errorMessage = '';
    
    if (errorValidator) {
      const result = errorValidator(inputElement);
      if (result instanceof Promise) {
        try {
          errorMessage = await result;
        } catch (error) {
          errorMessage = error as string;
        }
      } else {
        errorMessage = result;
      }
    }

    this.unsetCustomValidty(inputElement);
    this.hideErrorElementVisibility(inputElement);

    if (errorMessage) {
      inputElement.classList.add('is-invalid');
      inputElement.setCustomValidity(errorMessage);
      this.showErrorElementVisibility(inputElement);
    } else {
      if (inputElement.checkValidity()) {
        inputElement.classList.remove('is-invalid');
      } else {
        inputElement.classList.add('is-invalid');
        this.updateValidationMessage(inputElement);
        this.showErrorElementVisibility(inputElement);
      }
    }
  }, 500);

  private updateValidationMessage(element: HTMLInputElement) {
    const errorMessage = this.validationMessage()[element.id];
    if (typeof errorMessage === 'string') {
      element.setCustomValidity(errorMessage);
    } else if (typeof errorMessage === 'object') {
      const validityKey = this.getValidityErrorKey(element);
      const mappingKey = (validityKey && this.errorMessageKeyMapping[validityKey]) || 'required';

      if (typeof errorMessage[mappingKey] === 'function') element.setCustomValidity(errorMessage[mappingKey]());
      else element.setCustomValidity(errorMessage[mappingKey]);
    }
  }

  private getValidityErrorKey(element: HTMLInputElement) {
    return Object.keys(this.errorMessageKeyMapping).find((key) => element.validity[key]);
  }

  protected createErrorElement(element: HTMLInputElement): string {
    return `
      <p id="${element.id}-field-error" class="invalid-feedback mb-1 d-block">
        <uxt-icon icon="exclamation-triangle"></uxt-icon>
        <span class="visually-hidden">Error&nbsp;</span>${element.validationMessage}
      </p>
    `;
  }

  private showErrorElementVisibility(element: HTMLInputElement) {
    element.setAttribute('aria-invalid', 'true');
    if(element.validationMessage !== FORM_CONST.hideErrorMessage) {
      element.setAttribute('aria-describedby', `${element.id}-field-error`);
      this.errorContainer(element).insertAdjacentHTML('afterend', this.createErrorElement(element));
    }
  }

  private hideErrorElementVisibility(element: HTMLInputElement) {
    const nextElement = this.errorContainer(element).nextElementSibling;
    element.setAttribute('aria-invalid', 'false');
    element.removeAttribute('aria-describedby');
    if (nextElement?.classList.contains('invalid-feedback')) nextElement.remove();
  }

  errorContainer(element: HTMLInputElement) {
    return !!element.parentElement?.classList.contains('input-group') ? element.parentElement : element;
  }

  private unsetCustomValidty(element: HTMLInputElement) {
    element.setCustomValidity('');
  }

  protected validationMessage(): ValidationMessageType {
    throw new Error('Method not implemented.');
  }

  protected customValidator(): any {
    return false;
  }

  protected hideErrorMessage() {
    this.shadowRoot?.querySelector('.invalid-feedback')?.remove();
  }

  async isFormValid() {
    await this.validateFields();
    return this.formElement?.checkValidity();
  }

  protected getRawValue() {
    return Array.from(new FormData(this.formElement)).reduce((obj, [key, value]) => {
      obj[key] = value;
      return obj;
    }, {});
  }
}
